import { useEffect, useRef, useState } from "react"
import { Mic, MicOff } from "lucide-react"

const AvatarComponent = ({
  initials = "TE",
  isPiP = false,
  stream = null,
  isSelf = false,
  participantName = null,
}) => {
  const [audioLevel, setAudioLevel] = useState(0)
  const [isMicOn, setIsMicOn] = useState(false)
  const audioContextRef = useRef(null)
  const analyzerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const smoothedAudioLevelRef = useRef(0)

  // Generate color based on initials (only for non-self participants)
  const avatarColors = {
    blue: {
      base: "rgb(37, 99, 235)",
      gradient:
        "radial-gradient(circle at center, rgba(37, 99, 235, 0.5) 0%, rgba(29, 78, 216, 0.95) 100%)",
      ring: "rgba(96, 165, 250, {alpha})",
    },
    purple: {
      base: "rgb(147, 51, 234)",
      gradient:
        "radial-gradient(circle at center, rgba(147, 51, 234, 0.5) 0%, rgba(126, 34, 206, 0.95) 100%)",
      ring: "rgba(192, 132, 252, {alpha})",
    },
    green: {
      base: "rgb(22, 163, 74)",
      gradient:
        "radial-gradient(circle at center, rgba(22, 163, 74, 0.5) 0%, rgba(21, 128, 61, 0.95) 100%)",
      ring: "rgba(134, 239, 172, {alpha})",
    },
    yellow: {
      base: "rgb(202, 138, 4)",
      gradient:
        "radial-gradient(circle at center, rgba(202, 138, 4, 0.5) 0%, rgba(161, 98, 7, 0.95) 100%)",
      ring: "rgba(250, 204, 21, {alpha})",
    },
    pink: {
      base: "rgb(219, 39, 119)",
      gradient:
        "radial-gradient(circle at center, rgba(219, 39, 119, 0.5) 0%, rgba(190, 24, 93, 0.95) 100%)",
      ring: "rgba(244, 114, 182, {alpha})",
    },
    indigo: {
      base: "rgb(79, 70, 229)",
      gradient:
        "radial-gradient(circle at center, rgba(79, 70, 229, 0.5) 0%, rgba(67, 56, 202, 0.95) 100%)",
      ring: "rgba(129, 140, 248, {alpha})",
    },
    brown: {
      base: "rgb(118, 80, 66)",
      gradient:
        "radial-gradient(circle at center, rgba(148, 110, 92, 0.5) 0%, rgba(62, 42, 32, 0.95) 100%)",
      ring: "rgba(255, 182, 159, {alpha})",
    },
  }

  const getAvatarColor = () => {
    if (!isPiP) {
      return avatarColors.brown
    }

    // const colorKeys = ["blue", "purple", "green", "yellow", "pink", "yellow"]
    const colorKeys = ["yellow"]
    let hash = 0
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash)
    }
    const colorKey = colorKeys[Math.abs(hash) % colorKeys.length]
    return avatarColors[colorKey]
  }

  const selectedColor = getAvatarColor()

  // Add this useEffect to track mic status - FIXED VERSION
  useEffect(() => {
    if (!stream) {
      setIsMicOn(false)
      return
    }

    const checkMicStatus = () => {
      const audioTracks = stream.getAudioTracks()
      const hasEnabledAudio = audioTracks.some(
        (track) => track.enabled && track.readyState === "live"
      )
      setIsMicOn(hasEnabledAudio)
    }

    // Initial check
    checkMicStatus()

    const cleanupListeners = []

    // Set up listeners for each audio track
    const setupTrackListeners = (track) => {
      const handleMute = () => {
        console.log(`Audio track muted: ${track.id}`)
        checkMicStatus()
      }

      const handleUnmute = () => {
        console.log(`Audio track unmuted: ${track.id}`)
        checkMicStatus()
      }

      const handleEnded = () => {
        console.log(`Audio track ended: ${track.id}`)
        checkMicStatus()
      }

      track.addEventListener("mute", handleMute)
      track.addEventListener("unmute", handleUnmute)
      track.addEventListener("ended", handleEnded)

      cleanupListeners.push(() => {
        track.removeEventListener("mute", handleMute)
        track.removeEventListener("unmute", handleUnmute)
        track.removeEventListener("ended", handleEnded)
      })
    }

    // Set up listeners for existing tracks
    stream.getAudioTracks().forEach(setupTrackListeners)

    // Listen for new tracks being added
    const handleTrackAdded = (event) => {
      if (event.track.kind === "audio") {
        console.log(`Audio track added: ${event.track.id}`)
        setupTrackListeners(event.track)
        checkMicStatus()
      }
    }

    const handleTrackRemoved = (event) => {
      if (event.track.kind === "audio") {
        console.log(`Audio track removed: ${event.track.id}`)
        checkMicStatus()
      }
    }

    stream.addEventListener("addtrack", handleTrackAdded)
    stream.addEventListener("removetrack", handleTrackRemoved)

    // Also poll periodically as a fallback (every 500ms)
    const pollInterval = setInterval(checkMicStatus, 500)

    return () => {
      stream.removeEventListener("addtrack", handleTrackAdded)
      stream.removeEventListener("removetrack", handleTrackRemoved)
      cleanupListeners.forEach((cleanup) => cleanup())
      clearInterval(pollInterval)
    }
  }, [stream])

  useEffect(() => {
    if (!stream) {
      setAudioLevel(0)
      smoothedAudioLevelRef.current = 0
      return
    }

    const audioTracks = stream.getAudioTracks()
    if (audioTracks.length === 0) {
      setAudioLevel(0)
      smoothedAudioLevelRef.current = 0
      return
    }

    try {
      // Create audio context and analyzer
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)()
      const analyzer = audioContext.createAnalyser()
      analyzer.fftSize = 256
      analyzer.smoothingTimeConstant = 0.92 // Increased for smoother transitions

      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyzer)

      audioContextRef.current = audioContext
      analyzerRef.current = analyzer

      // Analyze audio levels
      const dataArray = new Uint8Array(analyzer.frequencyBinCount)

      const checkAudioLevel = () => {
        analyzer.getByteFrequencyData(dataArray)

        // Calculate average volume
        const average =
          dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length

        // Normalize to 0-1 range with some amplification
        const normalized = Math.min(average / 70, 1)

        // Apply additional smoothing via linear interpolation
        const smoothingFactor = 0.15 // Lower = smoother but more lag
        smoothedAudioLevelRef.current =
          smoothedAudioLevelRef.current * (1 - smoothingFactor) +
          normalized * smoothingFactor

        setAudioLevel(smoothedAudioLevelRef.current)
        animationFrameRef.current = requestAnimationFrame(checkAudioLevel)
      }

      checkAudioLevel()
    } catch (error) {
      console.error("Error setting up audio analysis:", error)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [stream])

  // Calculate base size for ring
  const avatarSize = isPiP ? 64 : 96
  const maxRingSize = avatarSize * 1.3 // 30% larger than avatar

  // Only show ring if audio level is above threshold
  const showRing = audioLevel > 0.1

  // Create multiple rings with different sizes and delays
  const numRings = 3
  const rings = Array.from({ length: numRings }, (_, i) => {
    const progress = (i + 1) / numRings
    const ringSize =
      avatarSize +
      (maxRingSize - avatarSize) * progress * (0.7 + audioLevel * 0.3)
    const opacity = audioLevel * (1 - progress * 0.4)
    return { ringSize, opacity, delay: i * 0.1 }
  })

  return (
    <div
      className="flex flex-col items-center justify-center h-full w-full rounded-3xl relative overflow-hidden"
      style={{ backgroundColor: selectedColor.base }}
    >
      {/* Radial gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: selectedColor.gradient,
        }}
      ></div>

      {/* Multiple glowing rings that pulse smoothly with audio */}
      <div className="absolute inset-0 flex items-center justify-center">
        {showRing &&
          rings.map((ring, index) => (
            <div
              key={index}
              className="absolute rounded-full transition-all ease-out"
              style={{
                width: `${ring.ringSize}px`,
                height: `${ring.ringSize}px`,
                border: `2px solid ${selectedColor.ring.replace(
                  "{alpha}",
                  (0.5 * ring.opacity).toString(),
                )}`,
                boxShadow: `
                0 0 ${20 * ring.opacity}px ${selectedColor.ring.replace(
                  "{alpha}",
                  (0.4 * ring.opacity).toString(),
                )},
                0 0 ${40 * ring.opacity}px ${selectedColor.ring.replace(
                  "{alpha}",
                  (0.2 * ring.opacity).toString(),
                )}
              `,
                opacity: ring.opacity > 0.1 ? ring.opacity : ring.opacity / 0.1,
                transitionDuration: `${300 + ring.delay * 100}ms`,
                transitionDelay: `${ring.delay * 50}ms`,
              }}
            />
          ))}
      </div>

      {/* Avatar circle with subtle pulsing effect */}
      <div className="relative z-10">
        <div
          className={`relative rounded-full flex items-center justify-center font-semibold transition-all duration-300 ease-out ${
            isPiP ? "h-16 w-16 text-base" : "h-32 w-32 text-3xl"
          }`}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            color: "rgb(255, 255, 255)",
            boxShadow: showRing
              ? `0 8px 32px 0 ${selectedColor.ring.replace(
                  "{alpha}",
                  (0.2 + audioLevel * 0.4).toString(),
                )}`
              : "0 8px 32px 0 rgba(0, 0, 0, 0.1)",
            transform: showRing
              ? `scale(${1 + audioLevel * 0.08})`
              : "scale(1)",
          }}
        >
          {participantName === "Emma" ? (
            <img
              src="https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1771424380/ai-agent_teuau4.png"
              alt="Emma"
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
      </div>

      {/* Participant name - bottom right glassmorphic */}
      {participantName && (
        <div
          className={`absolute bottom-3 right-3 z-20 ${
            isPiP ? "px-2 py-0.5" : "px-3 py-0.5"
          } rounded-lg flex items-center gap-1.5`}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        >
          {/* Microphone indicator */}
          {isMicOn ? (
            <Mic
              size={isPiP ? 12 : 14}
              className="text-white"
              style={{
                filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
              }}
            />
          ) : (
            <MicOff
              size={isPiP ? 12 : 14}
              className="text-red-400"
              style={{
                filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
              }}
            />
          )}

          <p
            className={`text-white font-medium ${
              isPiP ? "text-xs" : "text-sm"
            }`}
            style={{
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
            }}
          >
            {participantName}
          </p>
        </div>
      )}
    </div>
  )
}

export default AvatarComponent
